// 세계 법칙 주간(3개월 C6 C10b, 정본: docs/prd/c6-codex-alliance.md 4.5 · c6-codex-1-contract.md 4절).
// 규칙 7종 + 휴식, 13주 회전. 순수 데이터 + 순수 함수이며 시계를 읽지 않는다 — 규칙은 날짜(dayIndex)와 ctx만으로 정해지지만 날짜는 기기 시계다 — 기존 LIVE_WEEKS와 같은 한계로 서버 미검증이며, 배율 상한 1.3(ruleForRun이 자름)으로 이득을 제한한다.
// 주 번호는 liveops.ts의 liveWeekIndex와 같다(liveRuleSlot 훅). 보상 배율은 전부 WORLD_RULE_REWARD_MULT_MAX 이하다.
// 수치 근거(C6 L6 시뮬 tools/sim/codex720.ts, docs/loop/3month-highend/sim-codex.md): 규칙 주 수입(골드·EXP·강화석) 변화가 ±6.5% 안이 되게
//  몰려오는 밤의 몹 마릿수를 1.4/1.5 → 1.2/1.22, 노데스 서약의 골드·EXP를 1.3 → 1.06으로 낮췄다(처치 골드·EXP까지 곱하는 보수 가정에서도 안).
import { liveRuleSlot } from './liveops';

export type WorldRuleId = 'horde' | 'rift' | 'giant' | 'sprint' | 'nodeath' | 'elite' | 'worldboss' | 'rest';

export interface WorldRuleMods {
  /** 스테이지 몹 마릿수 배율 */ readonly mobCountMult: number;
  /** 콤보 유지 시간 배율 */ readonly comboKeepMult: number;
  /** 클리어 골드 배율(보상) */ readonly clearGoldMult: number;
  /** 보스 클리어 시 균열 조각 추가(0 = 없음) */ readonly riftShardBonus: number;
  /** 균열 조각 하루 상한(0 = 해당 없음) */ readonly riftShardDayCap: number;
  /** 보스 HP 배율 */ readonly bossHpMult: number;
  /** 보스 크기 배율 */ readonly bossScaleMult: number;
  /** 보상 카드 추가 장수(기본 3장에 더한다) */ readonly rewardCards: number;
  /** 이동 속도 배율 */ readonly moveSpeedMult: number;
  /** 별 시간 조건(60Hz tick). null = 기본 조건 */ readonly starTimeTicks: number | null;
  /** 별 3개 시 추가 강화석 */ readonly star3Stones: number;
  /** 받는 피해 배율 */ readonly damageTakenMult: number;
  /** 골드 보상 배율 */ readonly goldMult: number;
  /** EXP 보상 배율 */ readonly xpMult: number;
  /** 사망 시 판 종료(부활 없음, 재도전 가능) */ readonly deathEndsRun: boolean;
}
export interface WorldRuleDef {
  readonly id: WorldRuleId;
  readonly name: string;
  /** 출처 모드(기획서 표기) */
  readonly mode: string;
  /** 한국어 한 줄 설명 */
  readonly blurb: string;
  readonly mods: WorldRuleMods;
  /** 두 번째 등장(수치만 올린 판) */
  readonly strong?: boolean;
  readonly requires?: 'rebirth1' | 'eliteAffix' | 'fieldNet';
}
export interface WorldRuleCtx { readonly rebirth1: boolean; readonly eliteAffix: boolean; readonly fieldNet: boolean }

/** 서버 상한: 규칙이 거는 모든 보상 배율은 이 값 이하 */
export const WORLD_RULE_REWARD_MULT_MAX = 1.3;
/** 규칙 없음(휴식·레이드·파티·넷·마을)의 기본값: 곱은 1, 더하기는 0, 시간 조건 null, 사망 종료 없음 */
export const NEUTRAL_RULE_MODS: WorldRuleMods = {
  mobCountMult: 1, comboKeepMult: 1, clearGoldMult: 1, riftShardBonus: 0, riftShardDayCap: 0, bossHpMult: 1, bossScaleMult: 1, rewardCards: 0,
  moveSpeedMult: 1, starTimeTicks: null, star3Stones: 0, damageTakenMult: 1, goldMult: 1, xpMult: 1, deathEndsRun: false,
};
const M = (p: Partial<WorldRuleMods>): WorldRuleMods => ({ ...NEUTRAL_RULE_MODS, ...p });
const TICKS_PER_SEC = 60;

const HORDE: WorldRuleDef = { id: 'horde', name: '몰려오는 밤', mode: '핵앤슬래시', blurb: '몰려오는 밤 — 몹이 1.2배, 클리어 골드 +10%', mods: M({ mobCountMult: 1.2, comboKeepMult: 1.3, clearGoldMult: 1.1 }) };
const HORDE_STRONG: WorldRuleDef = { ...HORDE, strong: true, blurb: '몰려오는 밤(강) — 몹이 1.22배, 클리어 골드 +15%', mods: M({ mobCountMult: 1.22, comboKeepMult: 1.4, clearGoldMult: 1.15 }) };
const RIFT: WorldRuleDef = { id: 'rift', name: '균열의 숨결', mode: 'ARPG', blurb: '균열의 숨결 — 보스를 잡으면 균열 조각 +1 (하루 5개까지)', mods: M({ riftShardBonus: 1, riftShardDayCap: 5 }), requires: 'rebirth1' };
const GIANT: WorldRuleDef = { id: 'giant', name: '거인의 시대', mode: '보스', blurb: '거인의 시대 — 보스 HP 1.3배·크기 1.15배, 보상 카드 4장 중 선택', mods: M({ bossHpMult: 1.3, bossScaleMult: 1.15, rewardCards: 1 }) };
const GIANT_STRONG: WorldRuleDef = { ...GIANT, strong: true, blurb: '거인의 시대(강) — 보스 HP 1.4배·크기 1.2배, 보상 카드 4장 중 선택', mods: M({ bossHpMult: 1.4, bossScaleMult: 1.2, rewardCards: 1 }) };
const SPRINT: WorldRuleDef = { id: 'sprint', name: '질주의 주', mode: '스피드', blurb: '질주의 주 — 이동 +10%, 별 시간 3분, 별 3개면 강화석 +1', mods: M({ moveSpeedMult: 1.1, starTimeTicks: 180 * TICKS_PER_SEC, star3Stones: 1 }) };
const SPRINT_STRONG: WorldRuleDef = { ...SPRINT, strong: true, blurb: '질주의 주(강) — 이동 +12%, 별 시간 2분 40초, 별 3개면 강화석 +2', mods: M({ moveSpeedMult: 1.12, starTimeTicks: 160 * TICKS_PER_SEC, star3Stones: 2 }) };
const NODEATH: WorldRuleDef = { id: 'nodeath', name: '노데스 서약', mode: '위험 모드', blurb: '노데스 서약 — 받는 피해 +30%, 쓰러지면 판 종료, 골드·EXP 1.06배', mods: M({ damageTakenMult: 1.3, goldMult: 1.06, xpMult: 1.06, deathEndsRun: true }) };
const ELITE: WorldRuleDef = { id: 'elite', name: '정예의 주', mode: 'PoE 리그 메카닉', blurb: '정예의 주 — 정예 접두 몹이 더 자주 나와요', mods: NEUTRAL_RULE_MODS, requires: 'eliteAffix' };
const WORLDBOSS: WorldRuleDef = { id: 'worldboss', name: '월드 보스의 주', mode: 'MMO', blurb: '월드 보스의 주 — 필드에 월드 보스가 나타나요', mods: NEUTRAL_RULE_MODS, requires: 'fieldNet' };
const REST: WorldRuleDef = { id: 'rest', name: '휴식', mode: '휴식', blurb: '이번 주는 쉬어 가요', mods: NEUTRAL_RULE_MODS };

/** 정의 표(일반 판). 강한 판은 WORLD_RULE_STRONG */
export const WORLD_RULES: Readonly<Record<WorldRuleId, WorldRuleDef>> = { horde: HORDE, rift: RIFT, giant: GIANT, sprint: SPRINT, nodeath: NODEATH, elite: ELITE, worldboss: WORLDBOSS, rest: REST };
export const WORLD_RULE_STRONG: Readonly<Partial<Record<WorldRuleId, WorldRuleDef>>> = { horde: HORDE_STRONG, giant: GIANT_STRONG, sprint: SPRINT_STRONG };
export const WORLD_RULE_IDS: readonly WorldRuleId[] = ['horde', 'rift', 'giant', 'sprint', 'nodeath', 'elite', 'worldboss', 'rest'];

/** 13칸 회전(liveops 슬롯과 같은 주). worldboss는 회전에 없다(정의만) */
export const WORLD_RULE_ROTATION: readonly WorldRuleId[] = ['horde', 'giant', 'sprint', 'rift', 'nodeath', 'rest', 'horde', 'sprint', 'giant', 'rift', 'nodeath', 'rest', 'elite'];
/** 회전에서 수치가 오른 판을 쓰는 슬롯 */
export const WORLD_RULE_STRONG_SLOTS: readonly number[] = [6, 7, 8];

/** 슬롯의 규칙(조건 미충족 대체 전). 슬롯이 범위 밖이면 휴식 */
export function worldRuleOfSlot(slot: number): WorldRuleDef {
  if (!Number.isInteger(slot) || slot < 0 || slot >= WORLD_RULE_ROTATION.length) return REST;
  const id = WORLD_RULE_ROTATION[slot];
  return (WORLD_RULE_STRONG_SLOTS.includes(slot) && WORLD_RULE_STRONG[id]) || WORLD_RULES[id];
}
function met(def: WorldRuleDef, ctx: WorldRuleCtx): boolean {
  return def.requires === undefined || (def.requires === 'rebirth1' ? ctx.rebirth1 : def.requires === 'eliteAffix' ? ctx.eliteAffix : ctx.fieldNet);
}
/** 이 날짜의 규칙. 조건 미충족(균열: 1차 환생 전, 정예: 접두 데이터 없음, 월드 보스: 필드넷 꺼짐) → 몰려오는 밤, 시작일 이전 → 휴식 */
export function worldRuleOf(day: number, ctx: WorldRuleCtx): WorldRuleDef {
  const slot = liveRuleSlot(day);
  if (slot < 0) return REST;
  const def = worldRuleOfSlot(slot);
  return met(def, ctx) ? def : HORDE;
}

/** 한 판 보상 보정 중 가장 큰 배율(상한 검사용) */
export function maxRewardMult(m: WorldRuleMods): number { return Math.max(m.clearGoldMult, m.goldMult, m.xpMult); }
